// membersTab/styled.js
import { styled } from '@mui/material/styles';
import { 
  Paper, 
  Card, 
  Chip, 
  Box, 
  Button, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions,
  TextField,
  Tabs,
  Tab,
  Menu,
  MenuItem,
  IconButton
} from '@mui/material';

// Honey colors (consistent with your theme)
const honeyColors = {
  primary: '#F1C40F',
  primaryLight: '#F4D03F',
  primaryDark: '#D4AC0D',
  amber: '#FFB74D',
  cream: '#FFF8E1',
  honeycomb: '#D4A574',
  surface: '#FFFFFF',
  surfaceHoney: '#FFF9E6',
  surfaceGlass: 'rgba(255, 255, 255, 0.85)',
  border: '#F5E6B3',
  borderLight: '#FAF0C4',
  textPrimary: '#3E2723',
  textSecondary: '#5D4037',
  textMuted: '#8D6E63',
  warning: '#FF9800',
  info: '#29B6F6',
  success: '#66BB6A',
  error: '#EF5350',
  // Advanced gradients
  gradientPrimary: 'linear-gradient(135deg, #F1C40F 0%, #FFB74D 100%)',
  gradientHoneycomb: 'linear-gradient(135deg, #D4A574 0%, #F1C40F 50%, #FFB74D 100%)',
  gradientCard: 'linear-gradient(145deg, rgba(255,255,255,0.9) 0%, rgba(255,249,230,0.8) 100%)',
  // Advanced shadows
  shadowLight: '0 2px 8px rgba(212, 165, 116, 0.12)',
  shadowMedium: '0 4px 12px rgba(212, 165, 116, 0.18)',
  shadowHeavy: '0 8px 24px rgba(212, 165, 116, 0.25)',
  shadowGlow: '0 0 20px rgba(241, 196, 15, 0.3)',
  // Glass morphism
  backdropBlur: 'blur(20px)',
  backdropBlurLight: 'blur(10px)',
};

// Modern Search Box
export const SearchBox = styled(Paper)({
  display: 'flex',
  alignItems: 'center',
  padding: '12px 20px',
  borderRadius: '50px',
  background: honeyColors.surfaceGlass,
  backdropFilter: honeyColors.backdropBlurLight,
  border: `2px solid ${honeyColors.borderLight}`,
  boxShadow: honeyColors.shadowLight,
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  
  '&:hover': {
    borderColor: honeyColors.border,
    boxShadow: honeyColors.shadowMedium,
    transform: 'translateY(-2px)',
  },
  
  '&:focus-within': {
    borderColor: honeyColors.primary,
    boxShadow: `${honeyColors.shadowMedium}, 0 0 0 4px rgba(241, 196, 15, 0.15)`,
    transform: 'translateY(-2px)',
  },
  
  '& .MuiInputBase-root': {
    fontSize: '1rem',
    color: honeyColors.textPrimary,
    fontWeight: 500,
    
    '& input::placeholder': {
      color: honeyColors.textMuted,
      opacity: 1,
    },
  },
  
  '& .MuiSvgIcon-root': {
    color: honeyColors.textMuted,
    fontSize: '1.3rem',
  },
});

// Enhanced Member Card
export const MemberCard = styled(Card)({
  borderRadius: '20px',
  background: honeyColors.gradientCard,
  backdropFilter: honeyColors.backdropBlur,
  border: `2px solid ${honeyColors.borderLight}`,
  boxShadow: honeyColors.shadowLight,
  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  height: '100%',
  position: 'relative',
  overflow: 'hidden',
  
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '4px',
    background: honeyColors.gradientPrimary,
    opacity: 0,
    transition: 'opacity 0.3s ease',
  },
  
  '&:hover': {
    transform: 'translateY(-8px) scale(1.02)',
    borderColor: honeyColors.border,
    boxShadow: honeyColors.shadowHeavy,
    
    '&::before': {
      opacity: 1,
    },
  },
  
  '& .MuiCardContent-root': {
    position: 'relative',
    zIndex: 1,
  },
});

// Advanced Role Badge
export const RoleBadge = styled(Chip)(({ role }) => {
  const roleStyles = {
    'AD': { // Admin
      background: 'rgba(239, 83, 80, 0.15)',
      color: honeyColors.error,
      border: `1px solid rgba(239, 83, 80, 0.3)`,
    },
    'MA': { // Manager
      background: 'rgba(156, 39, 176, 0.15)',
      color: '#9c27b0',
      border: `1px solid rgba(156, 39, 176, 0.3)`,
    },
    'PA': { // Participant/Member
      background: 'rgba(102, 187, 106, 0.15)',
      color: honeyColors.success,
      border: `1px solid rgba(102, 187, 106, 0.3)`,
    },
  };
  
  const style = roleStyles[role] || {
    background: honeyColors.surfaceHoney,
    color: honeyColors.textSecondary,
    border: `1px solid ${honeyColors.border}`,
  };
  
  return {
    backgroundColor: style.background,
    color: style.color,
    border: style.border,
    fontWeight: 600,
    fontSize: '0.75rem',
    height: '28px',
    borderRadius: '14px',
    
    '& .MuiChip-label': {
      padding: '0 12px',
    },
  };
});

// Invite Button
export const InviteButton = styled(Button)({
  background: honeyColors.gradientPrimary,
  color: honeyColors.textPrimary,
  borderRadius: '50px',
  padding: '10px 24px',
  fontWeight: 700,
  fontSize: '0.9rem',
  boxShadow: honeyColors.shadowMedium,
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  textTransform: 'none',
  border: 'none',
  
  '&:hover': {
    background: honeyColors.gradientHoneycomb,
    transform: 'translateY(-2px)',
    boxShadow: honeyColors.shadowHeavy,
  },
  
  '& .MuiSvgIcon-root': {
    marginRight: '8px',
    fontSize: '1.1rem',
  },
});

// Enhanced Tabs Container
export const FilterTabsContainer = styled(Box)({
  borderBottom: `2px solid ${honeyColors.borderLight}`,
  marginBottom: '24px',
  position: 'relative',
  
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: '2px',
    background: honeyColors.gradientPrimary,
    opacity: 0.3,
  },
  
  '& .MuiTabs-root': {
    minHeight: 'auto',
    
    '& .MuiTabs-indicator': {
      backgroundColor: honeyColors.primary,
      height: '3px',
      borderRadius: '3px',
      boxShadow: honeyColors.shadowGlow,
    },
  },
  
  '& .MuiTab-root': {
    minHeight: 'auto',
    padding: '12px 20px',
    fontSize: '0.9rem',
    textTransform: 'none',
    fontWeight: 600,
    color: honeyColors.textSecondary,
    borderRadius: '12px 12px 0 0',
    margin: '0 4px',
    transition: 'all 0.2s ease',
    
    '&:hover': {
      backgroundColor: honeyColors.surfaceHoney,
      color: honeyColors.textPrimary,
    },
    
    '&.Mui-selected': {
      color: honeyColors.primary,
      fontWeight: 700,
      backgroundColor: honeyColors.cream,
    },
  },
});

// Member Avatar Container
export const MemberAvatarContainer = styled(Box)({
  position: 'relative',
  display: 'inline-block',
  
  '& .MuiAvatar-root': {
    width: '64px',
    height: '64px',
    border: `3px solid ${honeyColors.surface}`,
    boxShadow: honeyColors.shadowLight,
    transition: 'all 0.3s ease',
    
    '&.online': {
      borderColor: honeyColors.success,
      boxShadow: `${honeyColors.shadowLight}, 0 0 0 2px rgba(102, 187, 106, 0.3)`,
    },
  },
  
  // Online indicator dot
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: '4px',
    right: '4px',
    width: '16px',
    height: '16px',
    backgroundColor: honeyColors.success,
    border: `2px solid ${honeyColors.surface}`,
    borderRadius: '50%',
    opacity: 0,
    transition: 'opacity 0.3s ease',
  },
  
  '&.online::after': {
    opacity: 1,
  },
});

// Action Button Container
export const ActionButtonsContainer = styled(Box)({
  display: 'flex',
  gap: '8px',
  marginTop: '16px',
  
  '& .MuiIconButton-root': {
    backgroundColor: honeyColors.surfaceHoney,
    border: `1px solid ${honeyColors.borderLight}`,
    borderRadius: '12px',
    width: '40px',
    height: '40px',
    transition: 'all 0.2s ease',
    
    '&:hover': {
      backgroundColor: honeyColors.cream,
      borderColor: honeyColors.border,
      transform: 'scale(1.1)',
    },
    
    '&.primary': {
      backgroundColor: honeyColors.primary,
      color: honeyColors.textPrimary,
      
      '&:hover': {
        backgroundColor: honeyColors.primaryDark,
      },
    },
  },
});

// Enhanced Dialog Components
export const StyledDialog = styled(Dialog)({
  '& .MuiDialog-paper': {
    borderRadius: '24px',
    background: honeyColors.gradientCard,
    border: `2px solid ${honeyColors.border}`,
    boxShadow: honeyColors.shadowHeavy,
    backdropFilter: honeyColors.backdropBlur,
    overflow: 'visible',
    
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: '6px',
      background: honeyColors.gradientPrimary,
      borderRadius: '24px 24px 0 0',
    },
  },
  
  '& .MuiBackdrop-root': {
    backgroundColor: 'rgba(62, 39, 35, 0.4)',
    backdropFilter: 'blur(8px)',
  },
});

export const StyledDialogTitle = styled(DialogTitle)({
  background: 'transparent',
  padding: '24px 32px 16px',
  
  '& .MuiTypography-root': {
    fontWeight: 700,
    fontSize: '1.5rem',
    color: honeyColors.textPrimary,
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
});

export const StyledDialogContent = styled(DialogContent)({
  background: 'transparent',
  padding: '0 32px 24px',
  
  '& .MuiTextField-root': {
    marginBottom: '16px',
    
    '& .MuiOutlinedInput-root': {
      borderRadius: '16px',
      backgroundColor: honeyColors.surface,
      border: `1px solid ${honeyColors.borderLight}`,
      transition: 'all 0.2s ease',
      
      '&:hover': {
        borderColor: honeyColors.border,
        backgroundColor: honeyColors.surfaceHoney,
      },
      
      '&.Mui-focused': {
        borderColor: honeyColors.primary,
        backgroundColor: honeyColors.surface,
        boxShadow: `0 0 0 3px rgba(241, 196, 15, 0.1)`,
      },
      
      '& .MuiOutlinedInput-notchedOutline': {
        border: 'none',
      },
    },
    
    '& .MuiInputLabel-root': {
      color: honeyColors.textMuted,
      fontWeight: 500,
      
      '&.Mui-focused': {
        color: honeyColors.primary,
      },
    },
  },
});

export const StyledDialogActions = styled(DialogActions)({
  padding: '20px 32px 32px',
  backgroundColor: 'transparent',
  gap: '12px',
  
  '& .MuiButton-root': {
    borderRadius: '16px',
    textTransform: 'none',
    fontWeight: 600,
    padding: '10px 24px',
    fontSize: '0.9rem',
    
    '&.MuiButton-text': {
      color: honeyColors.textMuted,
      border: `1px solid ${honeyColors.borderLight}`,
      
      '&:hover': {
        backgroundColor: honeyColors.surfaceHoney,
        borderColor: honeyColors.border,
        color: honeyColors.textPrimary,
      },
    },
    
    '&.MuiButton-contained': {
      background: honeyColors.gradientPrimary,
      color: honeyColors.textPrimary,
      boxShadow: honeyColors.shadowMedium,
      border: 'none',
      
      '&:hover': {
        background: honeyColors.gradientHoneycomb,
        transform: 'translateY(-2px)',
        boxShadow: honeyColors.shadowHeavy,
      },
      
      '&:disabled': {
        background: honeyColors.textMuted,
        color: honeyColors.surface,
        opacity: 0.6,
        transform: 'none',
      },
    },
  },
});

// Enhanced Menu
export const StyledMenu = styled(Menu)({
  '& .MuiPaper-root': {
    borderRadius: '16px',
    background: honeyColors.surfaceGlass,
    backdropFilter: honeyColors.backdropBlur,
    border: `2px solid ${honeyColors.borderLight}`,
    boxShadow: honeyColors.shadowHeavy,
    minWidth: '200px',
    
    '& .MuiMenuItem-root': {
      padding: '12px 20px',
      fontSize: '0.9rem',
      fontWeight: 500,
      color: honeyColors.textPrimary,
      borderRadius: '12px',
      margin: '4px 8px',
      transition: 'all 0.2s ease',
      
      '&:hover': {
        backgroundColor: honeyColors.cream,
        color: honeyColors.textPrimary,
      },
      
      '&.danger': {
        color: honeyColors.error,
        
        '&:hover': {
          backgroundColor: 'rgba(239, 83, 80, 0.1)',
        },
      },
    },
  },
});

// Empty State Container
export const EmptyStateContainer = styled(Box)({
  textAlign: 'center',
  padding: '64px 32px',
  
  '& .MuiTypography-h6': {
    color: honeyColors.textSecondary,
    fontWeight: 600,
    marginBottom: '8px',
  },
  
  '& .MuiTypography-body2': {
    color: honeyColors.textMuted,
  },
});

// Member Info Section
export const MemberInfoSection = styled(Box)({
  '& .member-name': {
    fontWeight: 600,
    color: honeyColors.textPrimary,
    fontSize: '1.1rem',
    marginBottom: '4px',
  },
  
  '& .member-status': {
    fontSize: '0.85rem',
    
    '&.online': {
      color: honeyColors.success,
      fontWeight: 600,
    },
    
    '&.offline': {
      color: honeyColors.textMuted,
    },
  },
  
  '& .member-bio': {
    color: honeyColors.textSecondary,
    fontSize: '0.9rem',
    lineHeight: 1.4,
    marginBottom: '12px',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  },
  
  '& .member-detail': {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '8px',
    fontSize: '0.85rem',
    color: honeyColors.textMuted,
    
    '& .MuiSvgIcon-root': {
      fontSize: '1rem',
    },
  },
});

// Container for the entire members section
export const MembersContainer = styled(Box)({
  '& .members-header': {
    marginBottom: '32px',
    
    '& .search-invite-row': {
      display: 'flex',
      gap: '16px',
      alignItems: 'center',
      marginBottom: '24px',
    },
  },
});